import { CLEAN_COMMENT_LINKS_BELOW_KARMA, MODIO_SITE } from '@config'
import { AGREEMENT_TYPE_OPTIONS } from '@config/options.js'
import { hostUrl } from '@composables/useWindowEvents.js'
import { useEventListener } from '@vueuse/core'
import { Settings } from '@config/game.js'
import { useRoute } from '@composables'
import { gameStore } from '@stores'
import DOMPurify from 'dompurify'
import omit from 'lodash/omit.js'
import {
  USER_COLLECTION_BROWSE_ROUTE,
  USER_GAME_MOD_BROWSE_ROUTE,
  COLLECTION_BROWSE_ROUTE,
  USER_GUIDE_BROWSE_ROUTE,
  COLLECTION_VIEW_ROUTE,
  GUIDE_BROWSE_ROUTE,
  GUIDE_VIEW_ROUTE,
  MOD_BROWSE_ROUTE,
  MOD_VIEW_ROUTE,
  MOD_ADD_ROUTE,
} from '@config/routeNames.js'

const LINK_CONTENT_TYPE_OPTIONS = {
  COLLECTION: 'c',
  GUIDE: 'r',
  USER: 'u',
  MOD: 'm',
}

// Site regex patterns
const modioUrlRegex = `${MODIO_SITE}/(u|g)/([\\w_-]+)/?(${Object.values(LINK_CONTENT_TYPE_OPTIONS).join('|')})?/?([\\w_-]+)?/?(${Object.values(omit(LINK_CONTENT_TYPE_OPTIONS, 'USER')).join('|')})?`
const mentionRegex = `${MODIO_SITE}/(u|g)/[\\w_-]+(/(${Object.values(LINK_CONTENT_TYPE_OPTIONS).join('|')})/[\\w_-]+)?`

const typeRouteMap = {
  [LINK_CONTENT_TYPE_OPTIONS.COLLECTION]: {
    browseRoute: COLLECTION_BROWSE_ROUTE,
    viewRoute: COLLECTION_VIEW_ROUTE,
    param: 'collection',
  },
  [LINK_CONTENT_TYPE_OPTIONS.GUIDE]: {
    browseRoute: GUIDE_BROWSE_ROUTE,
    viewRoute: GUIDE_VIEW_ROUTE,
    param: 'guide',
  },
  [LINK_CONTENT_TYPE_OPTIONS.MOD]: {
    browseRoute: MOD_BROWSE_ROUTE,
    viewRoute: MOD_VIEW_ROUTE,
    addRoute: MOD_ADD_ROUTE,
    param: 'mod',
  },
  [LINK_CONTENT_TYPE_OPTIONS.USER]: {
    viewRoute: USER_GAME_MOD_BROWSE_ROUTE,
    param: 'user',
  },
  [`${LINK_CONTENT_TYPE_OPTIONS.USER}-${LINK_CONTENT_TYPE_OPTIONS.MOD}`]: {
    viewRoute: USER_GAME_MOD_BROWSE_ROUTE,
    param: 'user',
  },
  [`${LINK_CONTENT_TYPE_OPTIONS.USER}-${LINK_CONTENT_TYPE_OPTIONS.COLLECTION}`]:
    {
      viewRoute: USER_COLLECTION_BROWSE_ROUTE,
      param: 'user',
    },
  [`${LINK_CONTENT_TYPE_OPTIONS.USER}-${LINK_CONTENT_TYPE_OPTIONS.GUIDE}`]: {
    viewRoute: USER_GUIDE_BROWSE_ROUTE,
    param: 'user',
  },
}

let allowedLinkTypes = [
  LINK_CONTENT_TYPE_OPTIONS.USER,
  LINK_CONTENT_TYPE_OPTIONS.MOD,
]

if (Settings.value.showCollections) {
  allowedLinkTypes.push(LINK_CONTENT_TYPE_OPTIONS.COLLECTION)
}

if (Settings.value.showGuides) {
  allowedLinkTypes.push(LINK_CONTENT_TYPE_OPTIONS.GUIDE)
}

const game = gameStore().getPublicGame()

DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noreferrer noopener')
  }
})

function sanitize(text) {
  return DOMPurify.sanitize(text, {
    ADD_ATTR: ['target', 'frameborder', 'allowfullscreen', ':to'],
    ADD_TAGS: ['iframe', 'base-link'],
  })
}

export default function format(input, commentKarma = 1) {
  const mentionPattern = new RegExp(
    `<a href="${mentionRegex}">[\\w_-]+</a>`,
    'g'
  )
  const mentions = input.match(mentionPattern)

  let i = 0
  let formattedInput = input.replace(mentionPattern, () => `@MENTION_${i++}@`)

  if (commentKarma > CLEAN_COMMENT_LINKS_BELOW_KARMA) {
    formattedInput = formattedInput
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(
        /(^|[\s>]+)([\w]+?:\/\/[\w#$%&~/.\-;:=,?!@[\]+]*)/gi,
        '$1<a href="$2">$2</a>'
      )
      .replace(
        /(^|[\s>]+)((www|ftp)\.[\w#$%&~/.\-;:=,?!@[\]+]*)/gi,
        '$1<a href="https://$2">$2</a>'
      )
      .replace(
        /(^|[\s>]+)([a-z0-9\-_.]+)@([^[,<\s]+)/gi,
        '$1<a href="mailto:$2@$3">$2@$3</a>'
      )
  }

  if (!Settings.value.allowLinks) {
    formattedInput = stripLinks(formattedInput, true)
  }

  // Youtube embed
  formattedInput = formattedInput.replace(
    /<a href="https?:\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)[^"]*">[^<]+<\/a>/gi,
    '<iframe width="560" height="315" src="https://www.youtube.com/embed/$1" frameborder="0" allowfullscreen></iframe>'
  )

  // Add mentions back in
  formattedInput = formattedInput.replace(
    /@MENTION_(\d+)@/g,
    (match, index) => {
      if (mentions?.[index]) {
        return mentions[index].replace(/>([\w_-]+)</, '>@$1<')
      }
      return match
    }
  )

  // Strip {{ }} to prevent Vue injection
  formattedInput = formattedInput.replace(/{{/g, '{').replace(/}}/g, '}')

  const sanitized = sanitize(formattedInput)
  return sanitized
}

export function addBlankTargets(text) {
  return sanitize('' + text)
}

export function stripLinks(text, preserveModioLinks) {
  return ('' + text).replace(/(<a [^>]*>)(.*?)(<\/a>)/gi, (match, _, p2) => {
    return preserveModioLinks && text.includes(`href="${MODIO_SITE}`)
      ? match
      : `<u>${p2}</u>`
  })
}

export function formatAgreement(text, noLinks = false) {
  let result = text.replace(
    /<center>(.*?)<\/center>/g,
    '<div class="tw-m-auto tw-w-fit">$1</div>'
  )

  AGREEMENT_TYPE_OPTIONS.forEach(({ value, paths }) => {
    paths.forEach((path) => {
      const href = `https://mod.io/${path}`
      const regex = new RegExp(
        `<a\\s+href=["]${href}["][^>]*>(.*?)<\\/a>`,
        'gi'
      )
      result = result?.replace(
        regex,
        `<a href="${href}" @click.prevent="openAgreementModal(${value})">$1</a>`
      )
    })
  })

  // removes any existing target & rel attributes to prevent vue compile warning
  result = result
    ?.replace(new RegExp('target=["](.*?)["]', 'gi'), '')
    ?.replace(new RegExp('rel=["](.*?)["]', 'gi'), '')

  if (noLinks) {
    return stripLinks(text)
  }

  return ('' + result).replace(
    /<a\s+href=/gi,
    '<a target="_blank" rel="noreferrer noopener" href='
  )
}

export function formatDescription(text) {
  const div = document.createElement('div')
  div.innerHTML = text
  // Add link to all images that aren't direct descendants of a link
  div.querySelectorAll('img').forEach((n) => {
    if (n.parentElement.tagName !== 'A') {
      n.outerHTML = `<a href="${n.src}" target="_blank" rel="noreferrer noopener">${n.outerHTML}</a>`
    }
  })
  div.querySelectorAll('table').forEach((n) => {
    n.style.minWidth = '768px'
    n.outerHTML = `<div class="tw-overflow-auto">${n.outerHTML}</div>`
  })
  return div.innerHTML
}

export function attachLinkEvents(el) {
  const links = el.querySelectorAll('a')

  const hostRegex = `${hostUrl.value}#/?(${Object.values(LINK_CONTENT_TYPE_OPTIONS).join('|')})?/?([\\w_-]+)?/?(${Object.values(omit(LINK_CONTENT_TYPE_OPTIONS, 'USER')).join('|')})?`

  links.forEach((link) => {
    const hostMatch = link.href.match(hostRegex)
    const match = link.href.match(modioUrlRegex) ?? hostMatch
    if (match) {
      if (
        (match[3] && !allowedLinkTypes.includes(match[3])) ||
        (match[5] && !allowedLinkTypes.includes(match[5]))
      )
        return

      if (match[4] || match[2]) link.textContent = `@${match[4] || match[2]}`

      useEventListener(link, 'click', (e) => {
        let route = null

        if (match[1] == LINK_CONTENT_TYPE_OPTIONS.USER) {
          // User route
          route = {
            name: USER_GAME_MOD_BROWSE_ROUTE,
            params: {
              user: match[2],
            },
          }

          if (
            Object.values(omit(LINK_CONTENT_TYPE_OPTIONS, 'USER')).includes(
              match[3]
            )
          ) {
            route.name =
              typeRouteMap[
                `${LINK_CONTENT_TYPE_OPTIONS.USER}-${match[3]}`
              ].viewRoute
          }
        } else {
          // Game route
          if (match[2] !== game.value.name_id && !hostMatch) return

          const type = match[hostMatch ? 1 : 3]
          const name_id = match[hostMatch ? 2 : 4]
          const userResourceType = match[5]

          if (type) {
            if (name_id) {
              route = {
                name: typeRouteMap[type].viewRoute,
                params: {
                  [typeRouteMap[type].param]: name_id,
                },
              }
              if (userResourceType) {
                route.name =
                  typeRouteMap[
                    `${LINK_CONTENT_TYPE_OPTIONS.USER}-${userResourceType}`
                  ].viewRoute
              }
            } else if (typeRouteMap[type].browseRoute) {
              route = {
                name: typeRouteMap[type].browseRoute,
              }
            }
          } else {
            route = {
              name: typeRouteMap[LINK_CONTENT_TYPE_OPTIONS.MOD].browseRoute,
            }
          }
        }

        if (route) {
          e.preventDefault()
          useRoute().push(route)
        }
      })
    }
  })
}

export function formatCookLogSyntax(logs) {
  return (
    logs
      .replace(
        /(?<!\/)(\b(ERROR|FATAL|FAIL|Failed|Error)\b|\[Error\])(\s|:)/gi,
        '<span class="tw-text-danger tw-font-semibold">$1</span>$3'
      )
      .replace(
        /(?<!\/)(\b(WARN|WARNING|Warning)\b|\[Warning\])(\s|:)/gi,
        '<span class="tw-text-warning tw-font-semibold">$1</span>$3'
      )
      .replace(
        /(?<!\/)(\b(SUCCESS|Successful|Build Successful)\b)(\s|:)/gi,
        '<span class="tw-text-success tw-font-semibold">$1</span>$3'
      )
      // File paths and extensions (Windows drive letter paths and relative paths)
      .replace(
        /(\b[A-Za-z]:[/\\][\w\-_+. /\\]+(?:\.\w+)?|(?:\.\.\/)+[\w\-_+. /\\]+(?:\.\w+)?)/g,
        '<span class="tw-text-info tw-opacity-70 tw-italic">$1</span>'
      )
      // Stage markers with asterisks
      .replace(
        /(\*{5,}\s+[^*\n]+\s+\*{5,})/g,
        '<span class="tw-text-white tw-opacity-50 tw-font-medium">$1</span>'
      )
      // Brackets and parentheses content (excluding already matched log levels)
      .replace(
        /(\[(?!Warning|Error|Successful|WARN|ERROR|SUCCESS|\d{2}:\d{2}:\d{2})[^\]]*\])/g,
        '<span class="tw-text-white tw-opacity-50 tw-font-medium">$1</span>'
      )
  )
}
